import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  makeStyles,
  Fade,
  CircularProgress,
  Divider
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  History as HistoryIcon
} from '@material-ui/icons';
import { useDate } from '../../hooks/useDate';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({  searchContainer: {
    position: 'relative', // ✅ Mudando para relative para não sobrepor o header
    backgroundColor: theme.palette.background.paper,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // ✅ Ocupar a altura disponível
    flexShrink: 0, // ✅ Não permitir encolhimento
  },
  searchHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
    },
  },
  searchResults: {
    flex: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
  },
  searchStats: {
    padding: '8px 16px',
    backgroundColor: theme.palette.action.hover,
    fontSize: '13px',
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resultItem: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  activeResult: {
    backgroundColor: theme.palette.primary.main + '20',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  messagePreview: {
    fontSize: '14px',
    color: theme.palette.text.primary,
    marginBottom: 4,
    '& mark': {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
      padding: '2px 4px',
      borderRadius: 2,
      fontWeight: 500,
    },
  },
  messageInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingState: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  navigationButtons: {
    display: 'flex',
    gap: 4,
  },
  recentSearches: {
    padding: '16px',
  },
  recentSearchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 0',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  }
}));

const MessageSearch = ({ 
  chatId, 
  onClose, 
  onResultClick, 
  isOpen = false 
}) => {
  const classes = useStyles();
  const { datetimeToClient } = useDate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef();
  const debounceRef = useRef();

  // Carrega pesquisas recentes do localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`chat-search-recent-${chatId}`);
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (err) {
        console.error('Erro ao carregar pesquisas recentes:', err);
      }
    }
  }, [chatId]);

  // Foca no input quando abre
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Busca com debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, 300);
    } else {
      setSearchResults([]);
      setCurrentResultIndex(0);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, chatId]);

  const performSearch = async (term) => {
    if (!term.trim() || !chatId) return;

    setLoading(true);
    try {
      const { data } = await api.get(`/chats/${chatId}/messages/search`, {
        params: { 
          q: term,
          limit: 50 
        }
      });

      setSearchResults(data.messages || []);
      setCurrentResultIndex(0);
      
      // Salva na lista de pesquisas recentes
      saveRecentSearch(term);
      
    } catch (err) {
      console.error('Erro na busca:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const saveRecentSearch = (term) => {
    const updated = [
      term,
      ...recentSearches.filter(s => s !== term)
    ].slice(0, 5); // Mantém apenas 5 pesquisas recentes

    setRecentSearches(updated);
    localStorage.setItem(`chat-search-recent-${chatId}`, JSON.stringify(updated));
  };

  const highlightSearchTerm = (text, term) => {
    if (!term || !text) return text;
    
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  const handleResultClick = (message, index) => {
    setCurrentResultIndex(index);
    onResultClick && onResultClick(message);
  };

  const navigateResults = (direction) => {
    if (searchResults.length === 0) return;

    let newIndex;
    if (direction === 'up') {
      newIndex = currentResultIndex > 0 ? currentResultIndex - 1 : searchResults.length - 1;
    } else {
      newIndex = currentResultIndex < searchResults.length - 1 ? currentResultIndex + 1 : 0;
    }
    
    setCurrentResultIndex(newIndex);
    const message = searchResults[newIndex];
    onResultClick && onResultClick(message);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigateResults('up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigateResults('down');
    } else if (e.key === 'Enter' && searchResults.length > 0) {
      e.preventDefault();
      const message = searchResults[currentResultIndex];
      onResultClick && onResultClick(message);
    }
  };

  const handleRecentSearchClick = (term) => {
    setSearchTerm(term);
  };

  if (!isOpen) return null;

  return (
    <Fade in={isOpen}>
      <Paper className={classes.searchContainer} elevation={8}>
        {/* Header da busca */}
        <div className={classes.searchHeader}>
          <TextField
            ref={searchInputRef}
            className={classes.searchInput}
            placeholder="Buscar mensagens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          {searchResults.length > 0 && (
            <div className={classes.navigationButtons}>
              <IconButton 
                size="small" 
                onClick={() => navigateResults('up')}
                disabled={loading}
              >
                <ArrowUpIcon />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => navigateResults('down')}
                disabled={loading}
              >
                <ArrowDownIcon />
              </IconButton>
            </div>
          )}

          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        {/* Estatísticas da busca */}
        {(searchResults.length > 0 || loading) && (
          <div className={classes.searchStats}>
            <span>
              {loading ? 'Buscando...' : `${searchResults.length} resultado${searchResults.length !== 1 ? 's' : ''} encontrado${searchResults.length !== 1 ? 's' : ''}`}
            </span>
            {searchResults.length > 0 && (
              <span>
                {currentResultIndex + 1} de {searchResults.length}
              </span>
            )}
          </div>
        )}

        {/* Resultados da busca */}
        <div className={classes.searchResults}>
          {loading && (
            <div className={classes.loadingState}>
              <CircularProgress size={20} />
              <Typography variant="body2">Buscando mensagens...</Typography>
            </div>
          )}

          {!loading && searchTerm && searchResults.length === 0 && (
            <div className={classes.emptyState}>
              <Typography variant="body1">Nenhuma mensagem encontrada</Typography>
              <Typography variant="body2">
                Tente usar palavras-chave diferentes
              </Typography>
            </div>
          )}

          {!loading && !searchTerm && recentSearches.length > 0 && (
            <div className={classes.recentSearches}>
              <Typography variant="subtitle2" gutterBottom>
                Pesquisas recentes
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              {recentSearches.map((term, index) => (
                <div
                  key={index}
                  className={classes.recentSearchItem}
                  onClick={() => handleRecentSearchClick(term)}
                >
                  <HistoryIcon fontSize="small" />
                  <Typography variant="body2">{term}</Typography>
                </div>
              ))}
            </div>
          )}

          {!loading && searchResults.length > 0 && (
            <List disablePadding>
              {searchResults.map((message, index) => (
                <ListItem
                  key={message.id}
                  className={`${classes.resultItem} ${
                    index === currentResultIndex ? classes.activeResult : ''
                  }`}
                  onClick={() => handleResultClick(message, index)}
                >
                  <ListItemText
                    primary={
                      <div
                        className={classes.messagePreview}
                        dangerouslySetInnerHTML={{
                          __html: highlightSearchTerm(message.message, searchTerm)
                        }}
                      />
                    }
                    secondary={
                      <div className={classes.messageInfo}>
                        <span>{message.sender?.name || 'Usuário'}</span>
                        <span>{datetimeToClient(message.createdAt)}</span>
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </div>
      </Paper>
    </Fade>
  );
};

export default MessageSearch;
